import type { Container } from 'pixi.js';

// Combat camera — zoom + pan over the battle scene.
//
// Combat is a spectator phase: the player watches. So the camera does the
// looking for them, easing to frame whoever is attacking, while leaving
// manual pinch/drag available at any time.
//
// WHY IT SITS ON ITS OWN CONTAINER
// `eventPlayback.shakeStage()` writes `stage.x/y` directly and resets them
// to 0. If the camera drove the same transform, every screen shake would
// snap the camera back to the origin. The scene graph is therefore:
//
//   app.stage
//   ├── camera      ← this class owns scale + position
//   │   └── stage   ← shake owns x/y, unchanged
//   │       ├── bgLayer / units / fx
//   └── flash       ← screen-fixed, deliberately outside the camera
//
// Shake amplitude ends up multiplied by zoom, which reads correctly: a hit
// feels heavier when the camera is close.
//
// WHY minZoom IS 1 (FIT) AND NOT LOWER
// The scene is laid out to fit the canvas exactly at scale 1 (see
// CombatCanvas's fitScale). Allowing zoom below that would only add empty
// letterboxing, so "zoom out" bottoms out at "everything visible" — the
// locked floor. The ceiling is MAX_ZOOM.

/** Closest the player can get. Beyond ~2.5x the vector art starts to feel
    empty rather than detailed. */
export const MAX_ZOOM = 2.5;

/** How close the auto-follow camera sits during an exchange. Deliberately
    modest — enough to make the pan meaningful, not so close that you lose
    track of the rest of the board. */
const FOLLOW_ZOOM = 1.65;

/** After the player pans/zooms by hand, auto-follow stays out of the way
    this long before taking over again. */
const FOLLOW_RESUME_MS = 2500;

/** Per-frame easing factor toward the target, at 60fps. */
const EASE = 0.12;

export interface CameraViewport {
  worldW: number;
  worldH: number;
  viewW: number;
  viewH: number;
}

export class CombatCamera {
  /** Current (rendered) state. */
  private x: number;
  private y: number;
  private zoom = 1;

  /** Where we're easing toward. */
  private targetX: number;
  private targetY: number;
  private targetZoom = 1;

  private view: CameraViewport;

  /** Auto-follow is on until the player takes manual control. */
  private followEnabled = true;
  private manualUntil = 0;

  /** Reduced motion cuts instead of glides — continuous camera drift is a
      real nausea vector, so under this flag the camera snaps. */
  reducedMotion = false;

  /** Set false to park the camera at fit and ignore focus calls — used at
      4x playback, where following would just be a blur. */
  followSuppressed = false;

  /**
   * Locked = the pre-camera behaviour: fixed fit view, no follow, no
   * gestures. This is the DESKTOP DEFAULT — on a big screen the whole board
   * is already legible, so moving the camera takes away more than it adds.
   * Phones default to unlocked, where framing the exchange is the only way
   * to make units readable at all.
   */
  private locked = false;

  /** Monotonic clock, injected so the class stays testable and so this
      module never reaches for Date.now() directly. */
  private now: () => number;

  constructor(view: CameraViewport, now: () => number = () => performance.now()) {
    this.view = view;
    this.x = view.worldW / 2;
    this.y = view.worldH / 2;
    this.targetX = this.x;
    this.targetY = this.y;
    this.now = now;
  }

  /** Canvas resized — re-clamp so we can't be left looking out of bounds. */
  setViewport(view: CameraViewport) {
    this.view = view;
    this.targetZoom = this.clampZoom(this.targetZoom);
    const c = this.clampCenter(this.targetX, this.targetY, this.targetZoom);
    this.targetX = c.x;
    this.targetY = c.y;
  }

  private clampZoom(z: number): number {
    return Math.min(MAX_ZOOM, Math.max(1, z));
  }

  /**
   * Keep the visible rect inside the world.
   *
   * When the visible span is wider than the world (only possible at zoom 1
   * given minZoom, but resize races can produce it) the axis is centred
   * rather than clamped — otherwise min > max and the clamp inverts.
   */
  private clampCenter(x: number, y: number, zoom: number): { x: number; y: number } {
    const { worldW, worldH, viewW, viewH } = this.view;
    const halfW = viewW / zoom / 2;
    const halfH = viewH / zoom / 2;
    const cx = halfW * 2 >= worldW ? worldW / 2 : Math.min(worldW - halfW, Math.max(halfW, x));
    const cy = halfH * 2 >= worldH ? worldH / 2 : Math.min(worldH - halfH, Math.max(halfH, y));
    return { x: cx, y: cy };
  }

  get isLocked(): boolean {
    return this.locked;
  }

  /** Locking snaps back to the full-board view and drops manual control. */
  setLocked(locked: boolean) {
    this.locked = locked;
    if (locked) {
      this.manualUntil = 0;
      this.followEnabled = true;
      this.setTarget(this.view.worldW / 2, this.view.worldH / 2, 1);
    }
  }

  /** True while the player's manual control still owns the camera. */
  private inManualWindow(): boolean {
    return this.now() < this.manualUntil;
  }

  /** Any manual gesture suspends auto-follow for a grace period. */
  private markManual() {
    this.manualUntil = this.now() + FOLLOW_RESUME_MS;
  }

  /** Whether auto-follow may currently move the camera. */
  get isFollowing(): boolean {
    return (
      !this.locked &&
      this.followEnabled &&
      !this.followSuppressed &&
      !this.inManualWindow()
    );
  }

  /**
   * Frame an exchange. Called once per attack from the playback engine with
   * the two combatants' world positions.
   */
  focusOn(points: Array<{ x: number; y: number }>) {
    if (!this.isFollowing || points.length === 0) return;
    let sx = 0;
    let sy = 0;
    for (const p of points) {
      sx += p.x;
      sy += p.y;
    }
    this.setTarget(sx / points.length, sy / points.length, FOLLOW_ZOOM);
  }

  private setTarget(x: number, y: number, zoom: number) {
    const z = this.clampZoom(zoom);
    const c = this.clampCenter(x, y, z);
    this.targetX = c.x;
    this.targetY = c.y;
    this.targetZoom = z;
  }

  /** Snap back to the whole battlefield and hand control back to follow. */
  reset(opts: { resumeFollow?: boolean } = {}) {
    this.setTarget(this.view.worldW / 2, this.view.worldH / 2, 1);
    if (opts.resumeFollow) {
      this.followEnabled = true;
      this.manualUntil = 0;
    }
  }

  /** Drag pan. Delta is in screen pixels; divided by zoom to move in world. */
  panBy(dxScreen: number, dyScreen: number) {
    if (this.locked) return;
    this.markManual();
    this.setTarget(
      this.targetX - dxScreen / this.targetZoom,
      this.targetY - dyScreen / this.targetZoom,
      this.targetZoom,
    );
  }

  /**
   * Pinch / wheel zoom about a screen anchor, so the world point under the
   * fingers stays under the fingers.
   */
  zoomBy(factor: number, anchorScreenX?: number, anchorScreenY?: number) {
    if (this.locked) return;
    this.markManual();
    const nextZoom = this.clampZoom(this.targetZoom * factor);
    if (anchorScreenX === undefined || anchorScreenY === undefined) {
      this.setTarget(this.targetX, this.targetY, nextZoom);
      return;
    }
    // World point currently under the anchor.
    const { viewW, viewH } = this.view;
    const worldAtAnchorX = this.targetX + (anchorScreenX - viewW / 2) / this.targetZoom;
    const worldAtAnchorY = this.targetY + (anchorScreenY - viewH / 2) / this.targetZoom;
    // Choose a centre that keeps that world point under the anchor.
    const nextX = worldAtAnchorX - (anchorScreenX - viewW / 2) / nextZoom;
    const nextY = worldAtAnchorY - (anchorScreenY - viewH / 2) / nextZoom;
    this.setTarget(nextX, nextY, nextZoom);
  }

  /** Double-tap: toggle between fit and a close look at the tapped point. */
  toggleZoomAt(anchorScreenX: number, anchorScreenY: number) {
    if (this.locked) return;
    this.markManual();
    if (this.targetZoom > 1.05) {
      this.setTarget(this.view.worldW / 2, this.view.worldH / 2, 1);
      return;
    }
    const { viewW, viewH } = this.view;
    const worldX = this.targetX + (anchorScreenX - viewW / 2) / this.targetZoom;
    const worldY = this.targetY + (anchorScreenY - viewH / 2) / this.targetZoom;
    this.setTarget(worldX, worldY, FOLLOW_ZOOM);
  }

  /**
   * Advance the eased state and write it to the container.
   *
   * `deltaFrames` is PIXI's ticker delta (1 = 60fps frame), so easing stays
   * frame-rate independent — important on the cheap Android this targets.
   */
  update(container: Container, deltaFrames: number) {
    if (this.reducedMotion) {
      this.x = this.targetX;
      this.y = this.targetY;
      this.zoom = this.targetZoom;
    } else {
      // 1 - (1-k)^frames is the frame-rate-correct form of repeated lerp.
      const k = 1 - Math.pow(1 - EASE, Math.max(0.0001, deltaFrames));
      this.x += (this.targetX - this.x) * k;
      this.y += (this.targetY - this.y) * k;
      this.zoom += (this.targetZoom - this.zoom) * k;
    }
    const { viewW, viewH } = this.view;
    container.scale.set(this.zoom);
    container.position.set(
      -this.x * this.zoom + viewW / 2,
      -this.y * this.zoom + viewH / 2,
    );
  }

  /** For the overview button's active state. */
  get isZoomedIn(): boolean {
    return this.targetZoom > 1.05;
  }
}
